import { type ReactNode, useState, useCallback, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { LifeBuoy } from "lucide-react";
import { ChatwootLogo } from "@/components/icons/ChatwootLogo";
import { ChatwootEmbed } from "@/components/ChatwootEmbed";
import { AffineEmbed } from "@/components/AffineEmbed";
import LobeChatEmbed from "@/components/LobeChatEmbed/LobeChatEmbed";
import { AnimatedStarburst } from "@/components/icons/AnimatedStarburst";
import { useChatwootPulse } from "@/hooks/useChatwootPulse";
import { useChatPanelStore } from "@/lib/store/chatPanelStore";
import { useAiPanelStore } from "@/lib/store/aiPanelStore";
import { SupportPanelBody } from "@/features/support/widget/SupportPanelBody";
import { useSupportUnread } from "@/features/support/widget/useSupportUnread";
import { useAppAccess } from "@/hooks/useAppAccess";
import { RIGHT_RAIL_WIDTH, ZAVIAGO_AI_APP_KEY } from "./rightRailConstants";
import { RightRailPanel } from "./RightRailPanel";
import { pageContextStore } from "@/features/ai-context/pageContextStore";

export { RIGHT_RAIL_WIDTH };

function getRightRailIcon(name: string): ReactNode {
  if (name === "กล่องข้อความ") return <ChatwootLogo className="w-5 h-5" />;
  if (name === "โน้ต")
    return (
      <img
        src="/icons/affine-icon.png"
        alt="Affine"
        className="w-5 h-5 rounded-[4px]"
      />
    );
  if (name === "ช่วยเหลือ") return <LifeBuoy className="w-5 h-5 text-primary" />;
  if (name === "Zaviago AI") return <AnimatedStarburst size={20} />;
  return null;
}

const RIGHT_RAIL_ITEMS = [
  { id: "chat", name: "กล่องข้อความ", label: "แชท" },
  { id: "notes", name: "โน้ต", label: "โน้ต" },
  { id: "support", name: "ช่วยเหลือ", label: "ช่วยเหลือ" },
  { id: "ai", name: "Zaviago AI", label: "Zaviago AI" },
];

// ── Main Component ──────────────────────────────────────────

/**
 * Right-side app rail — Google Workspace style.
 * Clicking an icon opens a floating panel with iOS-style skeleton widgets.
 */
export function AppRailRight() {
  const [activePanel, setActivePanel] = useState<string | null>(null);
  // Lazy mount: only create iframes after first open, then keep them alive
  const [hasOpenedChat, setHasOpenedChat] = useState(false);
  const [hasOpenedNotes, setHasOpenedNotes] = useState(false);
  const [hasOpenedAi, setHasOpenedAi] = useState(false);
  const { data: chatwootPulse } = useChatwootPulse();
  const mineCount = chatwootPulse?.mine_count ?? 0;
  const supportUnread = useSupportUnread();
  // Zaviago AI is per-store opt-in: hidden until access has loaded AND the store has it
  // installed (no fail-open flash for stores that don't).
  const { appAccessLoaded, isInstalled } = useAppAccess();
  const aiEnabled = appAccessLoaded && isInstalled(ZAVIAGO_AI_APP_KEY);
  const railItems = RIGHT_RAIL_ITEMS.filter((item) => item.id !== "ai" || aiEnabled);

  // Open-at-conversation requests from elsewhere (e.g. Customer Activity feed).
  const chatReqNonce = useChatPanelStore((s) => s.nonce);
  const chatReqAccount = useChatPanelStore((s) => s.accountId);
  const chatReqConversation = useChatPanelStore((s) => s.conversationId);
  const chatPanelRef = useRef<HTMLDivElement | null>(null);
  const [chatInitialPath, setChatInitialPath] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (chatReqNonce === 0 || chatReqConversation == null) return;
    setActivePanel("chat");
    if (!hasOpenedChat) {
      // First open — mount the embed straight onto the conversation (SSO handles it).
      setChatInitialPath(`/conversations/${chatReqConversation}`);
      setHasOpenedChat(true);
    } else {
      // Already mounted + authed — point the iframe at the conversation.
      const iframe = chatPanelRef.current?.querySelector("iframe");
      if (iframe && chatReqAccount != null) {
        iframe.src = `https://inbox.zaviago.com/app/accounts/${chatReqAccount}/conversations/${chatReqConversation}`;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatReqNonce]);

  // Open-the-AI-panel requests from elsewhere (e.g. a Sheets dashboard's "ให้ AI สร้าง").
  const aiReqNonce = useAiPanelStore((s) => s.nonce);
  useEffect(() => {
    if (aiReqNonce === 0 || !aiEnabled) return;
    setActivePanel("ai");
    setHasOpenedAi(true);
    // Page-context re-post happens via the setAiPanelOpen(isAiVisible) effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiReqNonce]);

  const handleToggle = (id: string) => {
    const next = activePanel === id ? null : id;
    setActivePanel(next);
    if (id === "chat" && !hasOpenedChat) {
      setHasOpenedChat(true);
    }
    if (id === "notes" && !hasOpenedNotes) {
      setHasOpenedNotes(true);
    }
    if (id === "ai" && !hasOpenedAi) {
      setHasOpenedAi(true);
    }
  };

  const handleChatwootError = useCallback((message: string) => {
    console.error("[AppRailRight] Chatwoot error:", message);
  }, []);

  const handleAffineError = useCallback((message: string) => {
    console.error("[AppRailRight] Affine error:", message);
  }, []);

  const handleAiError = useCallback((message: string) => {
    console.error("[AppRailRight] Zaviago AI error:", message);
  }, []);

  const isChatVisible = activePanel === "chat";
  const isNotesVisible = activePanel === "notes";
  const isAiVisible = activePanel === "ai";
  const closePanel = useCallback(() => setActivePanel(null), []);

  // Page-aware AI: every open/close path (toggle, X, another panel, unmount) lands here.
  // Opening re-posts the page + screen snapshot at once; screen is only sent while open.
  useEffect(() => {
    pageContextStore.getState().setAiPanelOpen(isAiVisible);
  }, [isAiVisible]);
  useEffect(() => () => pageContextStore.getState().setAiPanelOpen(false), []);

  return (
    <>
      {/* Rail icons */}
      <div
        className="fixed z-40 flex flex-col items-center py-3 gap-1"
        style={{
          width: `${RIGHT_RAIL_WIDTH}px`,
          top: 0,
          right: 0,
          bottom: 0,
          background: "rgb(255 255 255 / 70%)",
          backdropFilter: "blur(6px) saturate(180%)",
          borderLeft: "1px solid #5e5d5d1a",
        }}
      >
        {railItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => handleToggle(item.id)}
            title={item.label}
            className={cn(
              "relative flex items-center justify-center w-9 h-9 rounded-full transition-all duration-150",
              activePanel === item.id
                ? "bg-blue-50 dark:bg-blue-900/20"
                : "hover:bg-gray-100/80 dark:hover:bg-gray-800/60"
            )}
          >
            {getRightRailIcon(item.name)}
            {item.id === "chat" && mineCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none">
                {mineCount > 99 ? "99+" : mineCount}
              </span>
            )}
            {item.id === "support" && supportUnread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none">
                {supportUnread > 99 ? "99+" : supportUnread}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Chat — mounted once, hidden/shown with CSS so the Chatwoot session stays alive */}
      {hasOpenedChat && (
        <RightRailPanel
          ref={chatPanelRef}
          visible={isChatVisible}
          icon={<ChatwootLogo className="w-5 h-5" />}
          title="แชท"
          onClose={closePanel}
        >
          <div className="flex-1 overflow-hidden relative">
            <ChatwootEmbed
              className="h-full w-full rounded-b-xl"
              initialPath={chatInitialPath}
              onError={handleChatwootError}
            />
          </div>
        </RightRailPanel>
      )}

      {/* Notes — mounted once, kept alive */}
      {hasOpenedNotes && (
        <RightRailPanel
          visible={isNotesVisible}
          icon={<img src="/icons/affine-icon.png" alt="Affine" className="w-5 h-5 rounded-[4px]" />}
          title="โน้ต"
          onClose={closePanel}
        >
          <div className="flex-1 overflow-hidden relative">
            <AffineEmbed className="h-full w-full rounded-b-xl" onError={handleAffineError} />
          </div>
        </RightRailPanel>
      )}

      {/* Support — rebuilt on every open (no iframe session to keep) */}
      {activePanel === "support" && (
        <RightRailPanel
          visible
          icon={<LifeBuoy className="w-5 h-5 text-primary" />}
          title="ช่วยเหลือ"
          onClose={closePanel}
        >
          <div className="flex-1 min-h-0 overflow-hidden">
            <SupportPanelBody onClose={closePanel} />
          </div>
        </RightRailPanel>
      )}

      {/* Zaviago AI (LobeHub) — mounted once, kept alive; `panel` = compact mobile layout */}
      {aiEnabled && hasOpenedAi && (
        <RightRailPanel
          visible={isAiVisible}
          icon={<AnimatedStarburst size={20} />}
          title="Zaviago AI"
          onClose={closePanel}
        >
          <div className="flex-1 overflow-hidden relative">
            <LobeChatEmbed className="h-full w-full rounded-b-xl" panel onError={handleAiError} />
          </div>
        </RightRailPanel>
      )}
    </>
  );
}
